import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2, Lock, Mail, User } from "lucide-react";
import { useUserStore } from "@/store/userStore";
import { Axios } from "@/utils/axios";
import { GoogleLogin } from "@react-oauth/google";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import OtpInput from "@/components/auth/OtpInput";
import { ROUTES } from "@/utils/routes";
import { Role } from "@/utils/constant";

const passwordRequirements =
  "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.";

const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#.^()[\]{}])[A-Za-z\d@$!%*?&#.^()[\]{}]{8,}$/;

const loginSchema = z.object({
  email: z
    .string({ required_error: "Email is required" })
    .email("Invalid email address"),
  password: z.string({ required_error: "Password is required" }),
  // .regex(passwordRegex, passwordRequirements),
});

const registerSchema = z
  .object({
    name: z.string().min(4, "Name must be at least 4 characters"),
    email: z
      .string({ required_error: "Email is required" })
      .nonempty("Email is required")
      .email("Invalid email address"),
    password: z
      .string({ required_error: "Password is required" })
      .regex(passwordRegex, passwordRequirements),
    confirmPassword: z
      .string({ required_error: "Confirm password is required" })
      .min(8, { message: "Confirm password must be at least 8 characters" }),
    termsAndCondition: z.boolean().refine((val) => val === true, {
      message: "You must accept the Terms & Conditions",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Confirm password does not match",
  });

type LoginFormData = z.infer<typeof loginSchema>;
type RegisterFormData = z.infer<typeof registerSchema>;

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [showOTP, setShowOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResendOTP, setIsResendOTP] = useState(false);
  const [userDataResponse, setUserDataResponse] = useState<any>(null);
  const formSchema = isLogin ? loginSchema : registerSchema;

  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login } = useUserStore();
  const [role, setRole] = useState(
    new URLSearchParams(location.search).get("type") || "freelancer"
  );

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    reset,
    formState: { errors },
  } = useForm<LoginFormData | RegisterFormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  // Cooldown timer for resend OTP
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prevState) => !prevState);
  };

  const handleGoogleResponse = async (response: any) => {
    setIsLoading(true);
    const idToken = response.credential;
    try {
      const response = await Axios.post("/api/auth/google-auth", {
        token: idToken,
        userType: role,
      });

      if ((response.statusText = "OK")) {
        setUserDataResponse({
          ...response.data.user,
          accessToken: response.data.accessToken,
        });

        login({
          email: response.data.user.email,
          name: response.data.user.name || "Demo User",
          userId: response.data.user.id,
          role: response.data.user.role,
          isPremium: true,
          accessToken: response.data.accessToken,
          isDetailsSaved: response.data?.user?.isDetailsSaved
        });

        if (role === Role.FREELANCER) {
          localStorage.setItem("showFreelancerModal", "true");
          localStorage.setItem(
            "userDataResponse",
            JSON.stringify({
              email: response.data.user.email,
              name: response.data.user.name || "Demo User",
              userId: response.data.user.id,
              role: response.data.user.role,
              isPremium: true,
              accessToken: response.data.accessToken,
            })
          );
        }

        if (role === Role.ENTERPRISE) {
          localStorage.setItem("showEnterpriseModal", "true");
          localStorage.setItem(
            "userDataResponse",
            JSON.stringify({
              email: response.data.user.email,
              name: response.data.user.name || "Demo User",
              userId: response.data.user.id,
              role: response.data.user.role,
              isPremium: true,
              accessToken: response.data.accessToken,
            })
          );
        }

        navigate(ROUTES.DASHBOARD);
      } else {
        navigate(ROUTES.AUTH);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Something went wrong. Please try again.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (formData: any) => {
    setIsLoading(true);
    const schema = isLogin ? loginSchema : registerSchema;
    const parsed = schema.safeParse(formData);

    if (!parsed.success) {
      const fieldErrors = parsed.error.flatten().fieldErrors;
      for (const key in fieldErrors) {
        const typedKey = key as keyof (LoginFormData & RegisterFormData);
        if (fieldErrors[typedKey]) {
          setError(typedKey, {
            type: "manual",
            message: fieldErrors[typedKey]?.[0],
          });
        }
      }
      setIsLoading(false);
      return;
    }

    try {
      if (isLogin) {
        const response = await Axios.post("api/auth/login", {
          email: formData.email,
          password: formData.password,
        });
        setUserDataResponse({...response.data.user,accessToken: response.data.accessToken});
        if (!response.data.user.isDetailsSaved && role === Role.FREELANCER) {
          localStorage.setItem("showFreelancerModal", "true");
          localStorage.setItem(
            "userDataResponse",
            JSON.stringify({
              email: response.data.user.email,
              name: response.data.user.name || "Demo User",
              userId: response.data.user.id,
              role: response.data.user.role,
              isPremium: true,
              accessToken: response.data.accessToken,
            })
          );
          // return;
        }
        if (!response.data.user.isDetailsSaved && role === Role.ENTERPRISE) {
          localStorage.setItem("showEnterpriseModal", "true");
          localStorage.setItem(
            "userDataResponse",
            JSON.stringify({
              email: response.data.user.email,
              name: response.data.user.name || "Demo User",
              userId: response.data.user.id,
              role: response.data.user.role,
              isPremium: true,
              accessToken: response.data.accessToken,
            })
          );
          // return;
        }

        toast({
          title: "Welcome back!",
          description: "Redirecting to your dashboard...",
        });

        await new Promise((resolve) => setTimeout(resolve, 1500));

        login({
          email: formData.email,
          name: formData.name || "Demo User",
          userId: response.data.user.id,
          role: response.data.user.role,
          isPremium: true,
          accessToken: response.data.accessToken,
          isDetailsSaved: response.data?.user?.isDetailsSaved
        });

        reset();
        navigate(ROUTES.DASHBOARD);
      } else {
        const response = await Axios.post("api/auth/register", {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role,
        });

        setUserDataResponse(response.data.user);
        toast({
          title: "Verification code sent!",
          description: "Please enter the 6-digit code sent to your email.",
        });

        setShowOtp(true);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Something went wrong. Please try again.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setIsLoading(true);
    const formData = getValues();
    try {
      const response = await Axios.post("/api/auth/confirm-code", {
        email: formData.email,
        code: otp,
      });

      setShowOtp(false);

      // toast({
      //   title: "Account verified successfully!",
      // });

      login({
        email: formData.email,
        name: userDataResponse?.name || "Demo User",
        userId: userDataResponse?.id,
        role: userDataResponse?.role,
        isPremium: true,
        accessToken: userDataResponse?.accessToken,
        isDetailsSaved: userDataResponse?.isDetailsSaved || false
      });

      navigate(ROUTES.DASHBOARD);

      // await new Promise((resolve) => setTimeout(resolve, 1500));

      if (!response.data.user.isDetailsSaved && role === Role.FREELANCER) {
        // handleCloseModal();
        localStorage.setItem("showFreelancerModal", "true");
        localStorage.setItem(
          "userDataResponse",
          JSON.stringify({
            email: formData.email,
            name: userDataResponse?.name || "Demo User",
            userId: userDataResponse?.id,
            role: userDataResponse?.role,
            isPremium: true,
            accessToken: userDataResponse?.accessToken,
          })
        );
      }

      if (!response.data.user.isDetailsSaved && role === Role.ENTERPRISE) {
        // handleEnterpriseCloseModal();
        localStorage.setItem("showEnterpriseModal", "true");
        localStorage.setItem(
          "userDataResponse",
          JSON.stringify({
            email: formData.email,
            name: userDataResponse?.name || "Demo User",
            userId: userDataResponse?.id,
            role: userDataResponse?.role,
            isPremium: true,
            accessToken: userDataResponse?.accessToken,
          })
        );
      }

      reset();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Invalid OTP. Please try again.";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
      setOtp(Array(6).fill(""));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    setIsResendOTP(true);
    try {
      await Axios.post("/api/auth/resend-code", {
        email: getValues("email"),
      });
      setResendCooldown(30);
      setIsResendOTP(false);
      toast({
        title: "New verification code sent!",
        description: "Please check your email for the new code.",
      });
    } catch (error) {
      setIsResendOTP(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to resend OTP. Please try again.",
      });
    } finally {
      setIsLoading(false);
      setIsResendOTP(false);
    }
  };

  return (
    <>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          {!isLogin && 
          <div className="grid grid-cols-2 border-b border-gray-100">
            <button
              className={`py-3 text-sm font-medium ${
                role === Role.FREELANCER
                  ? "bg-gradient-to-r from-sky-50 to-sky-100 text-sky-700 border-b-2 border-sky-500"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setRole("freelancer")}
            >
              Freelancer
            </button>
            <button
              className={`py-3 text-sm font-medium ${
                role === Role.ENTERPRISE
                  ? "bg-gradient-to-r from-sky-50 to-sky-100 text-sky-700 border-b-2 border-sky-500"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setRole("Enterprise")}
            >
              Enterprise
            </button>
          </div>}
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-emerald-600">
              {isLogin
                ? `Login`
                : `Create Account as ${
                    role === Role.FREELANCER ? "Freelancer" : "Enterprise"
                  }`}
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">{role === Role.FREELANCER ? "Full Name" : "Enterprise Name"}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input
                      id="name"
                      placeholder="John Doe"
                      className="pl-10"
                      disabled={showOTP}
                      {...register("name")}
                    />
                  </div>

                  {!isLogin && "name" in errors && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.name?.message}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10"
                    disabled={showOTP}
                    {...register("email")}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="******"
                    className="pl-10"
                    disabled={showOTP}
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-2.5"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="******"
                      className="pl-10"
                      disabled={showOTP}
                      {...register("confirmPassword")}
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-3 top-2.5"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {!isLogin && "confirmPassword" in errors && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.confirmPassword?.message}
                    </p>
                  )}
                </div>
              )}
              {!isLogin && (<div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="termsAndCondition"
                    className="w-4 h-4 accent-emerald-600 cursor-pointer"
                    {...register("termsAndCondition", {
                      required: "You must accept the Terms & Conditions",
                    })}
                  />
                  <label
                    htmlFor="termsAndCondition"
                    className="text-sm font-semibold text-gray-800 cursor-pointer"
                  >
                    I have read and agree to the{" "}
                    <Link to={
                      role === "freelancer"
                        ? "/terms-of-service-freelancer-contract"
                        : role === "Enterprise"
                          ? "/terms-of-service-enterprise-contract"
                          : "/terms-of-service"
                    } className="text-emerald-600 underline transition-colors duration-300">Terms & Conditions</Link>
                  </label>
                  {/* Display validation error */}
                </div>
                  {"termsAndCondition" in errors && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.termsAndCondition?.message}
                    </p>
                  )}
                  </div>
              )}

              {!isLogin && showOTP && (
                <div className="flex flex-col justify-center">
                  <h2 className="text-sm text-left font-semibold mb-4">
                    Enter Verification Code
                  </h2>
                  <OtpInput
                    otp={otp}
                    setOtp={setOtp}
                    onSubmit={handleVerifyOTP}
                  />
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendCooldown > 0 || isLoading}
                      className={`text-sm font-medium ${
                        resendCooldown > 0 || isLoading
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-emerald-600 hover:text-emerald-700"
                      }`}
                    >
                      {resendCooldown > 0
                        ? `Resend OTP in ${resendCooldown}s`
                        : "Resend OTP"}
                    </button>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                disabled={isLoading}
              >
                {isResendOTP
                  ? "Resend OTP"
                  : showOTP
                  ? "Verify OTP"
                  : isLogin
                  ? "Sign In"
                  : "Sign Up"}
                {isLoading && (
                  <div className="flex justify-center items-center py-12">
                    <Loader2 className="w-24 h-24 text-white-700 animate-spin" />
                  </div>
                )}
              </Button>

              <div className="flex justify-between">
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setShowOtp(false);
                      reset();
                    }}
                    className="text-sm text-emerald-600 hover:text-emerald-700"
                  >
                    {isLogin
                      ? "Need an account? Sign up"
                      : "Already have an account? Sign in"}
                  </button>
                </div>
                {isLogin && (
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(ROUTES.FORGOT_PASSWORD);
                      }}
                      className="text-sm text-emerald-600 hover:text-emerald-700"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Or continue with
                  </span>
                </div>
              </div>

              <div className="mt-3">
                <GoogleLogin
                  onSuccess={handleGoogleResponse}
                  onError={() => console.log("Login Failed")}
                  logo_alignment="center"
                  size="large"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Auth;
